/**
 * FmReferenceDeviationChart — an observed series against a labelled reference.
 *
 * Observed and reference draw from the neutral series sequence (ink solid,
 * amber dashed), never from a species slot. Null values are gaps; the signed
 * deviation is drawn only where both values are finite. Lines join served
 * points straight: a smoothed curve through daily values overshoots between
 * them (rainfall would dip below zero on a dry day next to a wet one).
 */
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import type {
  FmChartSize,
  FmReferenceDeviationChartSpec,
  FmReferenceDeviationPoint,
} from './chartTypes';
import {
  CHART_GRID_PROPS,
  chartMargin,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  xAxisTitle,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import { FmChartFrame } from './FmChartFrame';
import { REFERENCE_DEVIATION_COLORS } from './chartRoleColors';
import { SERIES_PATTERN_DASH } from './palette';

export type FmReferenceDeviationChartProps = Omit<FmReferenceDeviationChartSpec, 'kind'> & {
  size?: FmChartSize;
  'data-testid'?: string;
};

const DEVIATION_BAR_SIZE = 18;

function finite(value: number | null): value is number {
  return value !== null && Number.isFinite(value);
}

function plotRows(points: readonly FmReferenceDeviationPoint[]) {
  return points.map((point) => {
    const deviation =
      finite(point.observed) && finite(point.reference) ? point.observed - point.reference : null;
    return {
      label: point.label,
      observed: finite(point.observed) ? point.observed : null,
      reference: finite(point.reference) ? point.reference : null,
      surplus: deviation !== null && deviation >= 0 ? deviation : null,
      deficit: deviation !== null && deviation < 0 ? deviation : null,
    };
  });
}

export const FmReferenceDeviationChart: React.FC<FmReferenceDeviationChartProps> = ({
  points,
  xLabel,
  yLabel,
  observedLabel,
  referenceLabel,
  size = 'md',
  'data-testid': testId,
}) => (
  <FmChartFrame size={size} data-testid={testId} data-kind="reference-deviation">
    <ComposedChart data={plotRows(points)} margin={chartMargin(yLabel)} accessibilityLayer>
      <CartesianGrid {...CHART_GRID_PROPS} />
      <XAxis dataKey="label" {...CHART_XAXIS_PROPS} label={xAxisTitle(xLabel)} />
      <YAxis {...CHART_YAXIS_PROPS} label={yAxisTitle(yLabel)} />
      <Tooltip content={<FmChartTooltip />} />
      <Bar
        dataKey="surplus"
        fill={REFERENCE_DEVIATION_COLORS.surplus}
        legendType="none"
        tooltipType="none"
        maxBarSize={DEVIATION_BAR_SIZE}
        stackId="deviation"
        isAnimationActive={false}
      />
      <Bar
        dataKey="deficit"
        fill={REFERENCE_DEVIATION_COLORS.deficit}
        legendType="none"
        tooltipType="none"
        maxBarSize={DEVIATION_BAR_SIZE}
        stackId="deviation"
        isAnimationActive={false}
      />
      <Line
        type="linear"
        dataKey="observed"
        name={observedLabel}
        stroke={REFERENCE_DEVIATION_COLORS.observed}
        strokeWidth={2}
        dot={false}
        connectNulls={false}
        isAnimationActive={false}
      />
      <Line
        type="linear"
        dataKey="reference"
        name={referenceLabel}
        stroke={REFERENCE_DEVIATION_COLORS.reference}
        strokeWidth={2}
        strokeDasharray={SERIES_PATTERN_DASH.dashed}
        dot={false}
        connectNulls={false}
        isAnimationActive={false}
      />
    </ComposedChart>
  </FmChartFrame>
);
